import React, { useState, useEffect } from "react";
import HeatIcon from "./../../assets/Images/heat-black.png";
import { useLocation, useNavigate } from "react-router-dom";
import DataTable from "../shared/DataTable";
import { SystemAnalysisTableConfig } from "./SystemAnalysisTableConfig";
import { useBreadcrumb } from "../../context/BreadcrumbContext";
import { formatNumberWithCommas } from "../utils/numberFormat";

const SystemAnalysisType = () => {
  const { setAnalysisSubType } = useBreadcrumb();
  const navigate = useNavigate();
  const loc = useLocation();

  // State for selected tab and button
  const [selectedTab, setSelectedTab] = useState("");
  const [selectedButton, setSelectedButton] = useState("");

  // State for table data and headers
  const [filteredTableData, setFilteredTableData] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [cachedTableData, setCachedTableData] = useState({});

  // Get system analysis data from location state
  const systemAnalysisData = loc.state?.data || {};

  // Effect to handle initial loading and state restoration
  useEffect(() => {
    const savedTab = localStorage.getItem("systemAnalysisSelectedTab");
    const savedButton = localStorage.getItem("systemAnalysisSelectedButton");
    const savedTableData = JSON.parse(localStorage.getItem("systemAnalysisTableData") || "{}");

    let initialTab = savedTab || loc.state?.type || "LLT";

    setSelectedTab(initialTab);
    setAnalysisSubType(initialTab);

    // Restore button and table data if a tab and button were saved
    if (initialTab && savedButton) {
      setSelectedButton(savedButton);
      const cacheKey = `${initialTab}-${savedButton}`;
      if (savedTableData[cacheKey]) {
        setHeaders(savedTableData[cacheKey].headers);
        setFilteredTableData(savedTableData[cacheKey].data);
      } else {
      }
    } else {
      // If no saved button for the initial tab, clear button and table data
      setSelectedButton("");
      setFilteredTableData([]);
      setHeaders([]);
    }
    if (!loc.state && !savedTab) {
      navigate('/dashboard', { replace: true });
    }
 }, [loc.state, navigate, setAnalysisSubType, systemAnalysisData]);

  // Effect to handle clearing localStorage on component unmount or specific navigation
  useEffect(() => {
    const handleNavigationChange = () => {
      if (!window.location.href.includes('/dashboard/drawing') && !window.location.href.includes('/system-analysis')) {
        localStorage.removeItem("systemAnalysisSelectedTab");
        localStorage.removeItem("systemAnalysisSelectedButton");
        localStorage.removeItem("systemAnalysisTableData");
      }
    };

    window.addEventListener('beforeunload', handleNavigationChange);
    window.addEventListener('popstate', handleNavigationChange);

    return () => {
      window.removeEventListener('beforeunload', handleNavigationChange);
      window.removeEventListener('popstate', handleNavigationChange);
     if (!window.location.href.includes('/dashboard/drawing') && !window.location.href.includes('/system-analysis')) {
        localStorage.removeItem("systemAnalysisSelectedTab");
        localStorage.removeItem("systemAnalysisSelectedButton");
        localStorage.removeItem("systemAnalysisTableData");
      }
    };
  }, []); 

  const handleTabClick = (tab) => {
    setSelectedTab(tab);
    localStorage.setItem("systemAnalysisSelectedTab", tab);
    setAnalysisSubType(tab);

    const savedTableData = JSON.parse(localStorage.getItem("systemAnalysisTableData") || "{}");
    const cachedButton = Object.keys(savedTableData)
      .find(key => key.startsWith(`${tab}-`))
      ?.split('-')[1];

    if (cachedButton) {
      setSelectedButton(cachedButton);
      localStorage.setItem("systemAnalysisSelectedButton", cachedButton);
      const cacheKey = `${tab}-${cachedButton}`;
      setHeaders(savedTableData[cacheKey].headers);
      setFilteredTableData(savedTableData[cacheKey].data);
    } else {
      setSelectedButton("");
      setFilteredTableData([]);
      setHeaders([]);
      localStorage.removeItem("systemAnalysisSelectedButton");
    }
  };

  const heatExchangerTypeMapping = {
    'plateframe': 'Plate & Frame',
    'tubeintube': 'Tube In Tube',
    'shelltube': 'Shell & Tube',
    'plateshell': 'Plate & Shell',
    'scrappedsurfacecontherm': 'Scrapped Surface (Contherm)',
    'fallingfilminstant': 'Falling Film (Instant)',
    'icebuilder': 'Ice Builder',
    'Slurry': 'Slurry'
  };

  const handleButtonClick = (equipmentType) => {
    setSelectedButton(equipmentType);
    localStorage.setItem("systemAnalysisSelectedButton", equipmentType);


    const rawData = systemAnalysisData?.overlay?.equipementData[selectedTab]?.data[equipmentType]?.equipments || [];
    const { headers: newHeaders, mapping } = SystemAnalysisTableConfig[equipmentType] || { headers: [], mapping: {} };

    const transformedData = rawData.map((item) => {
      const row = {
        ...item,
      };

      // Handle Equipment ID with Plant IDs (each on new line)
      if (mapping["Equipment ID"]) {
        const plantIds = item.plantId || [];
        const equipmentName = item[mapping["Equipment ID"]] || '';

        // Remove duplicates and format Plant IDs with each on a new line
        const uniquePlantIds = [...new Set(plantIds)];
        const plantIdText = uniquePlantIds.length > 0
          ? `\n${uniquePlantIds.join('\n')}`
          : '';

        row["Equipment ID"] = `${equipmentName}${plantIdText}`;
      }

      // Handle Location (each unique value on new line)
      if (mapping["Location"]) {
        const locations = Array.isArray(item.location) ? item.location : [item.location];
        const uniqueLocations = [...new Set(locations.filter(loc => loc))];
        row["Location"] = uniqueLocations.join('\n');
      }

      newHeaders.forEach((header) => {
        if (header === "Equipment ID" || header === "Location") return;
        const mappedKey = mapping[header];
        let value = item[mappedKey];

        if (equipmentType === "Compressors" && header === "Type" && value) {
          value = value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
        }
        else if ((header === "Design Capacity (TR)" || header === "Load (TR)" || header === "Design Capacity (MBH)") && value) {
          value = formatNumberWithCommas(value);
        }
        else if (equipmentType === "Condensers" && header === "Type") {
          value = "Evaporative Condenser";
        }
        else if (header === "Heat Exchanger Type") {
          value = heatExchangerTypeMapping[value] || value;
        }

        row[header] = value || '';
      });
      return row;
    });

    setHeaders(newHeaders);
    setFilteredTableData(transformedData);

    // Cache the table data and headers
    setCachedTableData(prev => {
      const newCache = {
        ...prev,
        [`${selectedTab}-${equipmentType}`]: {
          headers: newHeaders,
          data: transformedData
        }
      };
      localStorage.setItem("systemAnalysisTableData", JSON.stringify(newCache));
      return newCache;
    });
  };
  return (
    <div className="container-fluid">
      {/* Tabs Section */}
      <div className="row rounded mx-2 theme-container">
        <div className="col-12 mt-2 p-4">
          <div className="tab-container">
            {['LLT', 'LT', 'MT', 'HT', 'HHT'].map((analysisType) => {
              const analysisData = systemAnalysisData?.overlay?.equipementData[analysisType];
              return (
                <button
                  key={analysisType}
                  onClick={() => handleTabClick(analysisType)}
                  style={{
                    borderColor: (analysisData?.issues === 0 && analysisData?.noIssues === 0)
                      ? "#aaa"
                      : (analysisData?.issues > 0)
                        ? "#fa6e33"
                        : (analysisData?.issues === 0 && analysisData?.noIssues > 0)
                          ? "#27ae60"
                          : "#aaa"
                  }}
                  disabled={analysisData?.issues === 0 && analysisData?.noIssues === 0}

                  className={`tab-card
                    ${(analysisData?.issues > 0 || (analysisData?.issues === 0 && analysisData?.noIssues > 0)) && selectedTab === analysisType ? "active" : ""}
                    ${(analysisData?.issues === 0 && analysisData?.noIssues === 0) ? "disabled" : ""}
                  `}

                >
                  <img src={HeatIcon} className="heat-icon" alt="Icon" />
                  {analysisType}
                </button>
              );
            })}

          </div>


          {/* Buttons Section */}
          {selectedTab && (
            <div style={{ display: "flex", gap: "5px", flexWrap: "wrap", marginTop: "20px" }}>
              {Object.keys(systemAnalysisData?.overlay?.equipementData[selectedTab]?.data || {}).map((equipmentType) => {
                const equipmentInfo = systemAnalysisData?.overlay?.equipementData[selectedTab]?.data[equipmentType];
                const hasEquipments = equipmentInfo?.equipments?.length > 0;
                const issues = equipmentInfo?.issues || 0;
                const noIssues = equipmentInfo?.noIssues || 0;

                let backgroundColor = "#aaa";
                let isDisabled = false;

                if (issues > 0) {
                  backgroundColor = "#fa6e33";
                } else if (issues === 0 && noIssues > 0) {
                  backgroundColor = "#4ad283";
                } else if (issues === 0 && noIssues === 0) {
                  isDisabled = true;
                }

                return (
                  <button
                    key={equipmentType}
                    onClick={() => handleButtonClick(equipmentType)}
                    style={{
                      backgroundColor,
                      border: selectedButton === equipmentType ? "1px solid" : "none",
                      cursor: isDisabled ? "not-allowed" : "pointer",
                      opacity: isDisabled ? 0.8 : 1
                    }}
                    className="analysis-buttons equipment-border"
                    disabled={isDisabled}
                  >
                    {equipmentType}
                  </button>
                );
              })}
            </div>
          )}


        </div>
      </div>

      {/* Table Section */}
      {selectedButton && (
        <div className="row rounded mx-2 theme-container mt-4">
          <div className="col-12px-4 py-2">
            <DataTable
              headingText={`${selectedTab} - ${selectedButton} with Issues`}
              headers={headers}
              data={filteredTableData}
            />
          </div>
        </div>
      )}
    </div>
  )
}

export default SystemAnalysisType;
